<template>
  <div>
    <b-sidebar
      id="sidebar-adjustment-details-adjustment"
      sidebar-class="sidebar-lg"
      :visible="adjustmentDetailsSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateAdjustmentDetailsSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4>Detalhes do ajuste</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <div class="m-2" v-if="adjustment">
          <b-row>
            <b-col cols="6" class="mb-1">
              Nome: 
            </b-col>
            <b-col cols="6" class="mb-1">
              {{ adjustment.name }} 
            </b-col>
            <b-col cols="6" class="mb-1">
              Status: 
            </b-col>
            <b-col cols="6" class="mb-1">
              <span class="text-primary"> {{ adjustment.status | adjustmentStatus}} </span>
            </b-col>
            <b-col cols="6" class="mb-1">
              Interessado: 
            </b-col>
            <b-col cols="6" class="mb-1" v-if="adjustment.consultant">
              {{ adjustment.consultant.name}} 
            </b-col>
            <b-col cols="6" class="mb-1" v-else-if="adjustment.department">
              {{ adjustment.department.name}} 
            </b-col>
            <b-col cols="6" class="mb-1">
              Data da solicitação:  
            </b-col>
            <b-col cols="6" class="mb-1" >
              {{ adjustment.launch_date | onlyDate}} 
            </b-col>
            <b-col cols="6" class="mb-1">
              Valor:  
            </b-col>
            <b-col cols="6" class="mb-1" >
              {{ adjustment.value | toCurrency}} 
            </b-col>
            <b-col cols="6" class="mb-1">
              Tipo do lançamento:  
            </b-col>
            <b-col cols="6" class="mb-1" >
              {{ adjustment.credit_type | creditType}} 
            </b-col>
            <b-col cols="6" class="mb-1">
              Motivo do lançamento:  
            </b-col>
            <b-col cols="6" class="mb-1" >
              {{adjustment.adjustment_reason? adjustment.adjustment_reason.name:''}} 
            </b-col>
            <b-col cols="6" class="mb-1">
              Data da fatura:  
            </b-col>
            <b-col cols="6" class="mb-1" >
              {{ adjustment.billing_date | onlyDate}} 
            </b-col>
            <b-col cols="6" class="mb-1">
              Data de faturamento:  
            </b-col>
            <b-col cols="6" class="mb-1" >
              {{ adjustment.billed_at | onlyDate}} 
            </b-col>
            <!-- A empresa pagadora decide de QUAL CNPJ sai a nota fiscal deste valor. A lista
                 já mostra a coluna; sem ela aqui, quem abre o lançamento para conferir não vê
                 justamente a informação que a task existe para produzir. -->
            <template v-if="adjustment.paying_company_id">
              <b-col cols="6" class="mb-1">
                Empresa pagadora:
              </b-col>
              <b-col cols="6" class="mb-1">
                <b-badge
                  :variant="payingCompanyVariant"
                  data-testid="financial-adjustment-paying-company"
                >
                  {{ payingCompanyName }}
                </b-badge>
              </b-col>
            </template>
            <!-- A competência que o ajuste CORRIGE, que não é a competência em que ele cai.
                 Sem esta linha, um ajuste que conserta março aparece na fatura de hoje sem
                 nenhuma marca de que se refere ao passado. -->
            <template v-if="adjustment.reference_competence">
              <b-col cols="6" class="mb-1">
                Competência de referência:
              </b-col>
              <b-col
                cols="6"
                class="mb-1"
                data-testid="financial-adjustment-reference-competence"
              >
                {{ adjustment.reference_competence }}
              </b-col>
            </template>
            <!--
              A FICHA DO CONTRATO, e não só o número.

              `#18271, #19177` obrigava o Financeiro a abrir a tela de contrato só para responder
              "é este contrato mesmo?". Com um contrato vinculado sai a ficha inteira; com dois ou
              mais, uma linha por contrato — a ficha não escala para a lista que ele cola da
              planilha, e o que importa em lista é identidade, não profundidade.
            -->
            <template v-if="linkedContracts.length">
              <b-col cols="12" class="mb-1">
                {{ linkedContracts.length === 1 ? "Contrato de origem:" : "Contratos de origem:" }}
              </b-col>
              <b-col
                cols="12"
                class="mb-1"
                data-testid="financial-adjustment-contracts"
              >
                <div
                  v-for="contract in linkedContracts"
                  :key="contract.id"
                  class="border rounded mb-1"
                >
                  <div class="d-flex align-items-start p-1">
                    <div class="flex-grow-1">
                      <div class="text-primary font-weight-bold">
                        #{{ contract.id }}
                      </div>
                      <div class="text-body">
                        {{ contractProductLabel(contract) }}
                      </div>
                      <div v-if="contract.customer" class="text-muted">
                        {{ contract.customer.name }}
                      </div>
                    </div>
                    <feather-icon icon="ExternalLinkIcon" size="16" class="text-primary" />
                  </div>
                  <!-- A ficha só na visão de um contrato. -->
                  <b-row v-if="linkedContracts.length === 1" class="px-1 pb-1">
                    <b-col cols="4" class="mb-1">
                      <small class="text-muted d-block">Valor</small>
                      {{ contract.gross_value | toCurrency }}
                    </b-col>
                    <b-col cols="4" class="mb-1">
                      <small class="text-muted d-block">Parcelas</small>
                      {{ contract.installments ? contract.installments + "x" : "—" }}
                    </b-col>
                    <b-col cols="4" class="mb-1">
                      <small class="text-muted d-block">Assinado em</small>
                      {{ contract.contract_date | onlyDate }}
                    </b-col>
                    <b-col cols="4" class="mb-1">
                      <small class="text-muted d-block">PN</small>
                      {{ contract.consultant ? contract.consultant.name : "—" }}
                    </b-col>
                    <b-col cols="4" class="mb-1">
                      <small class="text-muted d-block">Proposta</small>
                      {{ contract.proposal_number || "—" }}
                    </b-col>
                    <b-col cols="4" class="mb-1">
                      <small class="text-muted d-block">Situação</small>
                      {{ contract.contract_status ? contract.contract_status.name : "—" }}
                    </b-col>
                  </b-row>
                </div>
              </b-col>
            </template>
            <b-col cols="6" class="mb-1">
              Criado por:  
            </b-col>
            <b-col cols="6" class="mb-1" >
              {{ adjustment.user? adjustment.user.name:'' }}
            </b-col>
            <!--
              O RASTRO DA OPERAÇÃO EM LOTE.

              Antes era só a referência, como link para a tela `/lotes-financeiros` — um módulo
              inteiro para responder o que cabe aqui: quem mexeu, por quê, quando, e o que mais
              mudou junto. A tela foi cortada e o rastro veio para o lançamento que originou a
              pergunta.

              Guarda pela reference e não pelo objeto: sem ela não há nada a mostrar nem para onde
              linkar.
            -->
            <template v-if="batchOperationReference">
              <b-col cols="12">
                <div class="border rounded bg-light p-1 mb-1">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <small class="text-uppercase font-weight-bold text-body">
                      Origem do lançamento
                    </small>
                    <b-badge :variant="batchModeVariant">
                      {{ batchModeLabel }}
                    </b-badge>
                  </div>
                  <div
                    class="text-primary font-weight-bold"
                    data-testid="financial-adjustment-batch-origin-reference"
                  >
                    {{ batchOperationReference }}
                  </div>
                  <div class="text-body mb-1">
                    {{ batchOperation.title }}
                  </div>
                  <b-row>
                    <template v-if="batchOperation.reason">
                      <b-col cols="4" class="mb-1">Motivo</b-col>
                      <b-col cols="8" class="mb-1 text-body">{{ batchOperation.reason }}</b-col>
                    </template>
                    <template v-if="batchOperation.ticket_number">
                      <b-col cols="4" class="mb-1">Chamado</b-col>
                      <b-col cols="8" class="mb-1 text-body">{{ batchOperation.ticket_number }}</b-col>
                    </template>
                    <b-col cols="4" class="mb-1">Quem</b-col>
                    <b-col cols="8" class="mb-1 text-body">
                      {{ batchExecutedByLabel }}
                    </b-col>
                    <b-col cols="4" class="mb-1">Quando</b-col>
                    <b-col cols="8" class="mb-1 text-body">
                      {{ batchOperation.created_at | dateTime }}
                    </b-col>
                  </b-row>
                  <!--
                    §3.5 do card: "o Financeiro deve conseguir consultar PN, valor anterior,
                    ajuste, valor final, contrato/origem, motivo".

                    Quatro desses seis já estão nesta tela — o interessado, os contratos, o motivo
                    do lançamento e o motivo do lote. Faltavam os três números, e eles JÁ CHEGAVAM:
                    `by_entity` devolve `items[]` com old_value, new_value e diff, e o
                    `loadBatchOperation` descartava o campo.

                    Só o que esta operação fez NESTE lançamento. A quebra por PN de um lote inteiro
                    é outra pergunta, e ela tem resposta no link abaixo.
                  -->
                  <div v-if="batchItems.length" class="mt-1 pt-1 border-top">
                    <small class="text-uppercase font-weight-bold d-block mb-50">
                      O que mudou
                    </small>
                    <b-row
                      v-for="item in batchItems"
                      :key="item.id"
                      class="mb-50"
                      :data-testid="`financial-adjustment-batch-item-${item.id}`"
                    >
                      <b-col cols="12">
                        <small class="text-muted">{{ batchItemLabel(item) }}</small>
                      </b-col>
                      <b-col cols="4">
                        <small class="text-muted d-block">Valor anterior</small>
                        {{ item.old_value | toCurrency }}
                      </b-col>
                      <b-col cols="4">
                        <small class="text-muted d-block">Ajuste</small>
                        <!--
                          success/danger aqui são legítimos: o sinal do `diff` É crédito e débito,
                          que é o que essas cores significam nesta tela.
                        -->
                        <span :class="Number(item.diff) < 0 ? 'text-danger' : 'text-success'">
                          {{ item.diff | toCurrency }}
                        </span>
                      </b-col>
                      <b-col cols="4">
                        <small class="text-muted d-block">Valor final</small>
                        {{ item.new_value | toCurrency }}
                      </b-col>
                    </b-row>
                  </div>
                  <!--
                    Substitui a listagem da tela cortada: mesma tela de ajustes, filtrada pela
                    referência do lote. `status: []` é obrigatório — sem ele o default "pendente"
                    da lista esconderia justamente os lançamentos já aprovados do lote.
                  -->
                  <div class="text-right">
                    <router-link
                      :to="{
                        name: 'financial-adjustments-list',
                        query: { batch_reference: batchOperationReference, status: [] }
                      }"
                      target="_blank"
                      data-testid="financial-adjustment-batch-origin-link"
                    >
                      <small>
                        Ver os lançamentos deste lote
                        <feather-icon icon="ExternalLinkIcon" size="12" />
                      </small>
                    </router-link>
                  </div>
                  <div v-if="batchReadLabel" class="text-muted">
                    <feather-icon icon="CheckIcon" size="14" class="text-success" />
                    <small>{{ batchReadLabel }}</small>
                  </div>
                </div>
              </b-col>
            </template>
            <b-col cols="12" class="mb-1">
              Comentário:
            </b-col>
            <b-col cols="12" class="mb-1">
              <span v-safe-html="adjustment.comment"></span>
            </b-col>
          </b-row>
          <!-- O outro lado do vínculo: este ajuste não foi sacado e seguiu para a competência seguinte. -->
          <b-row v-if="adjustment.rolled_into_history">
            <b-col cols="12" class="mb-1">
              <feather-icon icon="CornerDownRightIcon" size="14" />
              Transferido para a
              <b-link
                data-testid="financial-adjustment-rolled-into-link"
                @click="openAdjustment(adjustment.rolled_into_history.financial_rolling_id)"
              >
                rolagem #{{ adjustment.rolled_into_history.financial_rolling_id }}
              </b-link>
            </b-col>
          </b-row>
          <template v-if="isRolling">
            <b-row>
              <b-col cols="12" class="mb-1">
                <hr>
                Composição da rolagem
                <hr>
              </b-col>
            </b-row>
            <rolling-items-details :rolling-id="adjustment.id">
              <!--
                Mesmo caminho do lote: a tela de ajustes filtrada pela rolagem, para usar os filtros
                e a ordenação de lá. `status: []` pelo mesmo motivo — o default "pendente" esconderia
                tudo, já que item de rolagem é sempre aprovado.
              -->
              <template #actions>
                <router-link
                  class="btn btn-flat-primary btn-sm mr-50"
                  :to="{
                    name: 'financial-adjustments-list',
                    query: { rolling_id: adjustment.id, status: [] }
                  }"
                  target="_blank"
                  data-testid="financial-adjustment-rolling-search-link"
                >
                  Ver os ajustes na busca
                  <feather-icon icon="ExternalLinkIcon" size="12" />
                </router-link>
              </template>
            </rolling-items-details>
          </template>
          <div class="d-flex mt-2 justify-content-between" v-if="pendingApproval">
            <b-button
              :disabled="saving"
              variant="danger"
              type="submit"
              class="payment-buttons"
              @click="updateStatus(adjustmentStatus.REJECTED_STATUS)"
            >
              {{ saving ? "Aguarde..." : "Reprovar" }}
            </b-button>
            <b-button
              :disabled="saving"
              variant="success"
              type="submit"
              class="payment-buttons"
              @click="updateStatus(adjustmentStatus.APPROVED_STATUS)"
            >
              {{ saving ? "Aguarde..." : "Aprovar" }}
            </b-button>
          </div>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton, BFormInput, BFormTextarea, BCol, BRow
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import Ripple from 'vue-ripple-directive'
import * as types from '../store/types'
import RollingItemsDetails from './RollingItemsDetails'
import * as financialBatchTypes from '@/modules/financial_batch/store/types'
import {
  ACKNOWLEDGED_REVIEW_STATUS,
  DRY_RUN_MODE,
} from '@/modules/financial_batch/components/financialBatchLabels'
import { FINANCIAL_ADJUSTMENT_APPROVAL, MANAGE_ACTION } from '@/constants/resources'
import * as adjustmentStatus from "@/constants/financial_adjustments";
import { PLATFORM_ID, payingCompanyLabel } from "@/constants/paying_companies";

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BCol,
    BRow,
    RollingItemsDetails,
  },
  directives: { 
    Ripple,
  },
  data() {
    return {
      adjustmentStatus,
      adjustment: undefined,
      batchOperation: undefined,
      batchItems: [],
      saving: false,
    }
  },
  setup() {
    return { toast: useToast()};
  },
  computed: {
    payingCompanyName() {
      // Reusa a mesma fonte da lista e do formulário: nome + CNPJ, porque nome sozinho não é
      // conferível contra a planilha que o Financeiro usa.
      return payingCompanyLabel(this.adjustment.paying_company_id)
    },
    payingCompanyVariant() {
      // secondary/info de propósito: nesta tela success e danger já significam crédito e débito.
      return Number(this.adjustment.paying_company_id) === PLATFORM_ID
        ? 'light-info'
        : 'light-secondary'
    },
    linkedContracts() {
      return this.adjustment.contracts || []
    },
    // O produto é o que deriva o CNPJ pagador, então ele vem antes do parceiro no rótulo.
    // `financial_solution` é `withTrashed()` no model: solução removida continua nomeando o
    // contrato antigo em vez de virar um espaço em branco.
    contractProductLabel() {
      return (contract) => {
        const parts = [
          contract.financial_solution ? contract.financial_solution.name : null,
          contract.partner_company ? contract.partner_company.name : null,
        ].filter(Boolean)
        return parts.length ? parts.join(' · ') : `Contrato #${contract.id}`
      }
    },
    batchModeLabel() {
      return this.batchOperation && this.batchOperation.mode === DRY_RUN_MODE
        ? 'Simulação'
        : 'Aplicado'
    },
    // ⚠️ Simulação NÃO pode sair verde: um lote em dry-run não moveu dinheiro nenhum, e pintá-lo
    // como aplicado é a diferença entre "isto explica o seu saldo" e "isto não explica nada".
    batchModeVariant() {
      return this.batchOperation && this.batchOperation.mode === DRY_RUN_MODE
        ? 'light-secondary'
        : 'light-success'
    },
    batchExecutedByLabel() {
      if (!this.batchOperation) return ''
      const parts = [
        this.batchOperation.executed_by_name,
        this.batchOperation.executed_by_role_name,
      ].filter(Boolean)
      // Operação detectada não tem autor declarado — foi o observer que a criou sozinho, e dizer
      // isso é mais útil do que deixar o campo vazio.
      return parts.length ? parts.join(' · ') : 'não declarado'
    },
    /**
     * O que a linha do item representa, em português.
     *
     * `field` é o nome da coluna alterada (`value`, `percentage_value`…) e `action` diz se foi
     * criação, alteração ou remoção. Sem isto, três números soltos não dizem do que são.
     */
    batchItemLabel() {
      return (item) => {
        const acoes = {
          create: 'Criado',
          created: 'Criado',
          update: 'Alterado',
          updated: 'Alterado',
          delete: 'Removido',
          deleted: 'Removido',
        }
        const acao = acoes[item.action] || item.action || 'Alterado'
        const partes = [acao]

        if (item.field) partes.push(`campo ${item.field}`)
        if (item.competence) partes.push(`competência ${item.competence}`)

        return partes.join(' · ')
      }
    },
    batchReadLabel() {
      if (!this.batchOperation || this.batchOperation.review_status !== ACKNOWLEDGED_REVIEW_STATUS) {
        return ''
      }
      const who = this.batchOperation.reviewed_by_name
      return who ? `Lido por ${who}` : 'Lido pelo Financeiro'
    },
    ...mapGetters({
      adjustmentDetailsSidebar: types.FINANCIAL_ADJUSTMENT_DETAILS_SIDEBAR
    }),
    batchOperationReference() {
      return this.batchOperation ? this.batchOperation.reference : undefined;
    },
    canUpdateStatus() {
      return this.$can(MANAGE_ACTION, FINANCIAL_ADJUSTMENT_APPROVAL);
    },
    // As duas grafias existem na base legada.
    isRolling() {
      return this.adjustment && ['Rolagem', 'ROLAGEM'].includes(this.adjustment.type);
    },
    pendingApproval() {
      return this.adjustment && 
        this.adjustment.status === adjustmentStatus.PENDING_STATUS && 
        this.canUpdateStatus;
    }
  },
  methods: {
    ...mapMutations({
      mutateAdjustmentDetailsSidebar: types.MUTATE_FINANCIAL_ADJUSTMENT_DETAILS_SIDEBAR
    }),
    ...mapActions({
      getAdjustment: types.GET_FINANCIAL_ADJUSTMENT,
      updateAdjustment: types.UPDATE_FINANCIAL_ADJUSTMENT_STATUS,
      getBatchesByEntity: financialBatchTypes.GET_FINANCIAL_BATCHES_BY_ENTITY,
    }),
    // A maioria dos ajustes não vem de lote: o endpoint responde 200 com operation null.
    // Nesse caso não exibimos nada e o silêncio é o comportamento esperado.
    loadBatchOperation() {
      this.getBatchesByEntity({
        type: 'FinancialAdjustment',
        id: this.adjustmentDetailsSidebar.id,
      })
        .then(response => {
          const body = (response && response.data) || {}
          const fromList = Array.isArray(body.data) ? body.data[0] : undefined
          this.batchOperation = body.operation || fromList || undefined
          // `items` vinha na resposta desde sempre e era descartado aqui — são os três números
          // que o §3.5 pede e que a tela não tinha.
          this.batchItems = Array.isArray(body.items) ? body.items : []
        })
        .catch(() => {
          this.batchOperation = undefined
          this.batchItems = []
        })
    },
    onShow(){
      this.loadBatchOperation()
      this.getAdjustment(this.adjustmentDetailsSidebar.id)
        .then(response => {
          if(response.data.id){
            this.adjustment = response.data
          }else{
            throw new DOMException("Report não encontrado.")
          }
          
        })
        .catch(err => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os detalhes do ajute. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          this.hide
        })
    },
    updateStatus(status){
      this.saving = true
      this.updateAdjustment({
        id: this.adjustmentDetailsSidebar.id,
        status: status
      })
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: "Status atualizado com sucesso.",
              icon: "CheckIcon",
              variant: "success",
            },
          });
          this.adjustmentDetailsSidebar.saveAction()
          this.mutateAdjustmentDetailsSidebar({ visible: false })
        })
        .catch(err => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao atualizar o status do ajuste. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.saving = false
        })
    },
    openAdjustment(id){
      this.adjustment = undefined
      this.batchOperation = undefined
      this.batchItems = []
      this.mutateAdjustmentDetailsSidebar({ id })
      this.onShow()
    },
    clear(){
      this.adjustment = undefined
      this.batchOperation = undefined
      this.mutateAdjustmentDetailsSidebar({ id: undefined })
    }
  }
}
</script>

<style lang="scss">
.sidebar-bottom {
  position: absolute;
  bottom: 0;
  width: -moz-available;          /* WebKit-based browsers will ignore this. */
  width: -webkit-fill-available;  /* Mozilla-based browsers will ignore this. */
  width: fill-available;
}

.payment-buttons {
  width: 11rem;
}
</style>
